import Link from 'next/link';
import React from 'react';
import styled, { css } from 'styled-components';

import BreadCrumbs from '@/components/atom/BreadCrumbs';
import Divider from '@/components/atom/Divider';
import MaterialIcon from '@/components/atom/MaterialIcon';
import Chevron from '@/components/atom/icon/Chevron';
import NonF2fDiagnosisBannerIcon from '@/components/atom/icon/hospitals/NonF2fDiagnosisBannerIcon';
import LinkTag from '@/components/common/LinkTag';
import ListMagazineLine from '@/components/common/communication/magazine/ListMagazineLine';
import TemplateLayout from '@/components/layouts/TemplateLayout';
import EmptyHospital from '@/components/templates/hospital/neighborhood/components/EmptyHospital';
import EmptyNeighborhoodSearchResult from '@/components/templates/hospital/neighborhood/components/EmptyNeighborhoodSearchResult';
import NeighborhoodSearchDoctorItem from '@/components/templates/hospital/neighborhood/components/NeighborhoodSearchDoctorItem';
import NeighborhoodSearchHospitalItem from '@/components/templates/hospital/neighborhood/components/NeighborhoodSearchHospitalItem';
import RelatedKeywordContentList from '@/components/templates/nonBenefit/home/components/RelatedKeywordContentList';
import SearchAddressFilterModal from '@/components/templates/nonBenefit/home/components/SearchAddressFilterModal';
import useNeighborhoodHospitalViewModel from '@/hooks/hospital/neighborhood/useNeighborhoodHospitalViewModel';
import { NeighborhoodHospitalFilter } from '@/models/hospital';
import theme from '@/styles/theme';
import { CustomPageProps } from '@/types/app';
import { DIRECTION, ROUTES } from '@/types/common';
import { ContractType, HospitalSearchMode, HospitalSearchSortType } from '@/types/hospital';
import { addQueryString } from '@/utils/url';

interface Props extends CustomPageProps {
  viewModel: ReturnType<typeof useNeighborhoodHospitalViewModel>;
}

const NeighborhoodHospitalTemplate = ({ client, viewModel }: Props) => {
  return (
    <TemplateLayout.Wrapper>
      <BreadCrumbsWrapper>
        <BreadCrumbs
          visible={!client.isMobile}
          type={'hospitals'}
          path={ROUTES.HOSPITAL.NEIGHBORHOOD.path + addQueryString(viewModel.queries)}
        />
      </BreadCrumbsWrapper>
      <TemplateLayout.Content>
        <NeighborhoodSection>
          <HospitalTitleWrapper>
            <Header>
              <HeaderTitle>{`${viewModel.koreaAddress.getAddressTitle(viewModel.queryRegion)} ${
                viewModel.queries.keyword
              } ${viewModel.neighborhoodHospitalTypes.selectedTypeTitle(
                viewModel.queries.type
              )} 병원 (2025)`}</HeaderTitle>
            </Header>
            <HeaderDescription>{`닥터나우에서 ${viewModel.koreaAddress.getAddressTitle(viewModel.queryRegion)} ${
              viewModel.queries.keyword
            } ${viewModel.neighborhoodHospitalTypes.selectedTypeTitle(
              viewModel.queries.type
            )} 병원 정보를 조회한 정보에요.`}</HeaderDescription>
          </HospitalTitleWrapper>
          <HospitalFilterOptionWrapper>
            <FilterButton onClick={viewModel.openNeighborhoodHospitalTypeBottomSheet}>
              {`${viewModel.neighborhoodHospitalTypes.selectedTypeTitle(viewModel.queries.type)} 병원`}
              <MaterialIcon name={'chevron_right'} size={16} color={theme.color.gray700} rotate={90} filled />
            </FilterButton>
            <FilterButton onClick={viewModel.openSearchAddressFilterModal}>
              <MaterialIcon name={'my_location'} size={16} color={theme.color.gray700} filled />
              {`${viewModel.koreaAddress.getAddressTitle(viewModel.queryRegion)}`}
            </FilterButton>
            <FilterButton onClick={viewModel.openNeighborhoodKeywordBottomSheet}>
              {`${viewModel.queries.keyword}`}
              <MaterialIcon name={'chevron_right'} size={16} color={theme.color.gray700} rotate={90} filled />
            </FilterButton>
            <FilterButton
              selected={viewModel.queries.filter === NeighborhoodHospitalFilter.NonF2fHospital}
              onClick={viewModel.toggleNonF2fHospitalFilter}
            >
              <MaterialIcon name={'call'} size={16} color={theme.color.gray700} filled />
              비대면 진료
            </FilterButton>
          </HospitalFilterOptionWrapper>
          <HospitalListWrapper>
            {!viewModel.hospitals.length && !viewModel.isLoadingHospitals && <EmptyHospital />}
            {!viewModel.isLoadingHospitals &&
              viewModel.hospitals.length > 0 &&
              viewModel.hospitals.map((hospital, index) => {
                return (
                  <>
                    <Link
                      key={`event-${hospital.hospitalPublicId}`}
                      href={`${ROUTES.HOSPITAL.DETAIL.path}${hospital.hospitalPublicId}`}
                    >
                      <a target={'_self'}>
                        <NeighborhoodSearchHospitalItem
                          adBanners={hospital.hospitalAdBanners}
                          hospitalPid={hospital.hospitalPublicId}
                          status={hospital.hospitalOperationStatus}
                          name={hospital.hospitalName}
                          rating={hospital.reviewScore}
                          reviewTotalCount={hospital.reviewTotalCount}
                          badges={hospital.hospitalBadges}
                          imageUrl={hospital.hospitalRepresentativeImageUrl}
                          address={hospital.hospitalAddress}
                          isBookable={hospital.isBookable}
                          representativeDepartmentName={hospital.hospitalRepresentativeDepartmentName}
                          operationDisplay={hospital.operationDisplay}
                          hasNonF2fDoctors={
                            !!hospital.doctors.find((doctor) => doctor.contractType === ContractType.All)
                          }
                          isLastItem={
                            index === viewModel.hospitals.length - 1 || (index === 1 && !!viewModel.matchedCategory)
                          }
                        />
                      </a>
                    </Link>
                    {index === 1 && viewModel.matchedCategory && (
                      <NonF2fDoctorListBannerWrapper>
                        <NonF2fDoctorListBanner onClick={viewModel.openAppInstallBottomSheet}>
                          <NonF2fDoctorListBannerContent>
                            <NonF2fDiagnosisBannerIcon />
                            <NonF2fDcotorListBannerTextArea>
                              <NonF2fDoctorListBannerTitle>
                                {`${viewModel.koreaAddress.getAddressTitle(viewModel.queryRegion)} ${
                                  viewModel.queries.keyword
                                }`}{' '}
                                비대면 진료 받기
                              </NonF2fDoctorListBannerTitle>
                              <NonF2fDoctorListBannerDescription>
                                병원 가지 않아도 진료를 볼 수 있어요
                              </NonF2fDoctorListBannerDescription>
                            </NonF2fDcotorListBannerTextArea>
                          </NonF2fDoctorListBannerContent>
                          <Chevron direction={DIRECTION.RIGHT} width={12} height={12} color={theme.color.gray300} />
                        </NonF2fDoctorListBanner>
                      </NonF2fDoctorListBannerWrapper>
                    )}
                  </>
                );
              })}
          </HospitalListWrapper>
          <div className={'w-full px-[24px] py-[20px]'}>
            <Link
              href={
                ROUTES.HOSPITAL.SEARCH.path +
                addQueryString({
                  keyword: viewModel.queries.keyword,
                  mode: HospitalSearchMode.SEARCH,
                  sortType: HospitalSearchSortType.ACCURACY,
                })
              }
            >
              <a>
                <button
                  className={
                    'typography-ui-15sb flex h-[40px] w-full items-center justify-center gap-[4px] rounded-[8px] border-[1px] border-gray-100 border-solid text-gray-600'
                  }
                >
                  병원 더보기
                  <MaterialIcon name={'keyboard_arrow_right'} size={20} color={theme.color.gray600} filled />
                </button>
              </a>
            </Link>
          </div>
        </NeighborhoodSection>
        <Divider height={'16px'} color={theme.color.gray30} />
        <NeighborhoodSection>
          <NeighborhoodTopWrapper>
            <NeighborhoodTitle>
              {client.isMobile
                ? `${viewModel.koreaAddress.getAddressTitle(viewModel.queryRegion)} ${
                    viewModel.queries.keyword
                  }\n${viewModel.neighborhoodHospitalTypes.selectedTypeTitle(
                    viewModel.queries.type
                  )} 병원에서\n 비대면 진료를 받을 수 있어요`
                : `${viewModel.koreaAddress.getAddressTitle(viewModel.queryRegion)} ${
                    viewModel.queries.keyword
                  } ${viewModel.neighborhoodHospitalTypes.selectedTypeTitle(
                    viewModel.queries.type
                  )} 병원에서\n 비대면 진료를 받을 수 있어요`}
            </NeighborhoodTitle>
            <NeighborhoodDescription>{`병원에 가기 힘들다면, ${viewModel.koreaAddress.getAddressTitle(
              viewModel.queryRegion
            )} ${viewModel.queries.keyword} 선생님에게 전화로 진료를 받아보세요.`}</NeighborhoodDescription>
          </NeighborhoodTopWrapper>
          {!viewModel.isLoadingHospitals && viewModel.nonF2fDoctors.length === 0 && (
            <EmptyNeighborhoodSearchResult
              title={'가까운 의사가 없어요'}
              description={'닥터나우 앱으로 \n병원에 방문하지 않아도 진료 받을 수 있어요'}
              noticeIcon={
                <EmptyImage width={200} src={'/images/neighborhood/empty_hospital.png'} alt={'의사 안내 이미지'} />
              }
              button={
                <EmptyButton onClick={viewModel.openAppInstallBottomSheet}>비대면으로 편하게 진료 보기</EmptyButton>
              }
            />
          )}
          {!viewModel.isLoadingHospitals && viewModel.nonF2fDoctors.length > 0 && (
            <>
              <DoctorListWrapper>
                {viewModel.nonF2fDoctors.map((doctor, index) => (
                  <Link
                    key={`neighborhood-doctor-${doctor.publicId}`}
                    href={`${ROUTES.DOCTOR.DETAIL.path}/${doctor.publicId}`}
                  >
                    <a target={'_self'}>
                      <NeighborhoodSearchDoctorItem
                        key={`neighborhood-doctor-${doctor.publicId}`}
                        doctorName={doctor.doctorName}
                        hospitalName={doctor.hospitalName}
                        doctorProfileImageUrl={doctor.profileImageUrl}
                        reviewRating={doctor.reviewAverageRating}
                        reviewTotalCount={doctor.reviewTotalCount}
                        videoCallAvailable={doctor.readyForVideoCall}
                        professionalDepartmentName={doctor.professionalDepartmentName}
                        isLastItem={index === viewModel.nonF2fDoctors.length - 1}
                      />
                    </a>
                  </Link>
                ))}
              </DoctorListWrapper>
              <DoctorButtonWrapper>
                <PrimarySearchButton
                  onClick={viewModel.openAppInstallBottomSheet}
                >{`${viewModel.koreaAddress.getAddressTitle(viewModel.queryRegion)} ${
                  viewModel.queries.keyword
                } 진료 편하게 보기`}</PrimarySearchButton>
              </DoctorButtonWrapper>
            </>
          )}
        </NeighborhoodSection>
        {viewModel.faqs.length > 0 && (
          <>
            <Divider height={'16px'} color={theme.color.gray30} />
            <MagazineSection>
              <RelatedKeywordContentList
                list={viewModel.faqs}
                title={`${viewModel.queries.keyword} 자주 묻는 질문`}
                renderItem={(faq, isLast) => {
                  return (
                    <LinkTag
                      key={`faq-${faq.magazinePid}`}
                      href={`${ROUTES.FAQ.DETAIL.path}/${faq.magazinePid}`}
                      onClickNavigateToScreen={() => viewModel.onClickRelatedFaq(faq.magazinePid)}
                    >
                      <ListMagazineLine
                        magazine={{
                          title: faq.title,
                          mobileTitle: faq.title,
                          image: '',
                          date: faq.createdDate,
                          magazinePid: faq.magazinePid,
                          description: faq.description,
                          mobileDescription: faq.mobileDescription,
                        }}
                        isLast={isLast}
                      />
                    </LinkTag>
                  );
                }}
              />
            </MagazineSection>
          </>
        )}
        {viewModel.magazines.length > 0 && (
          <>
            <Divider height={'16px'} color={theme.color.gray30} />
            <MagazineSection>
              <RelatedKeywordContentList
                list={viewModel.magazines}
                title={`${viewModel.queries.keyword} 건강 매거진`}
                renderItem={(magazine, isLast) => {
                  return (
                    <LinkTag
                      key={`magazine-${magazine.magazinePid}`}
                      href={`${ROUTES.MAGAZINE.DETAIL.path}/${magazine.magazinePid}`}
                      onClickNavigateToScreen={() => viewModel.onClickRelatedMagazine(magazine.magazinePid)}
                    >
                      <ListMagazineLine
                        magazine={{
                          title: magazine.title,
                          mobileTitle: magazine.title,
                          image: magazine.titleImageUrl,
                          date: magazine.createdDate,
                          magazinePid: magazine.magazinePid,
                          description: magazine.description,
                          mobileDescription: magazine.mobileDescription,
                        }}
                        isLast={isLast}
                      />
                    </LinkTag>
                  );
                }}
              />
            </MagazineSection>
          </>
        )}
      </TemplateLayout.Content>
      {/* 각종 파라미터가 매번 바로 업데이트되어야하는데 showModal 사용시 업데이트가 안되서 기존 방식으로 작업함 */}
      {viewModel.isAddressFilterModalOpen && (
        <SearchAddressFilterModal {...viewModel.getSearchAddressFilterModalProps()} />
      )}
    </TemplateLayout.Wrapper>
  );
};

export default NeighborhoodHospitalTemplate;

const BreadCrumbsWrapper = styled.div`
  padding: ${({ theme }) => (theme.client.isMobile ? '0' : '24px 24px 16px')};
`;

const Header = styled.header``;

const HospitalTitleWrapper = styled.div`
  padding: 2px 24px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 8px;
`;

const HeaderTitle = styled.h1`
  ${({ theme }) => css`
    ${theme.typography.UI22SB}
    color: ${theme.color.gray900};
  `}
`;

const HeaderDescription = styled.p`
  ${({ theme }) => css`
    ${theme.typography.UI15R}
    color: ${theme.color.gray500};
  `}
`;

const HospitalFilterOptionWrapper = styled.div`
  padding: 12px 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  overflow-x: scroll;
  white-space: nowrap;
  margin-bottom: 8px;
`;

const FilterButton = styled.button<{ selected?: boolean }>`
  ${({ theme, selected }) => css`
    display: flex;
    align-items: center;
    gap: 2px;
    padding: 0 14px;
    height: 36px;
    ${theme.typography.UI14R}
    color: ${selected ? theme.color.primary700 : theme.color.gray700};
    background-color: ${selected ? theme.color.primary100 : theme.color.gray30};
    border: 1px solid ${selected ? theme.color.primary700 : theme.color.gray30};
    border-radius: 40px;
    transition: background-color, color, border 0.2s;
    & > div > span {
      color: ${selected ? theme.color.primary700 : theme.color.gray700};
      transition: color 0.2s;
    }
  `}
`;

const HospitalListWrapper = styled.ul`
  display: flex;
  flex-direction: column;
`;

const DoctorListWrapper = styled(HospitalListWrapper)``;

const NonF2fDoctorListBannerWrapper = styled.div`
  display: flex;
  width: 100%;
  padding: 20px;
  flex-direction: column;
  align-items: flex-start;
`;

const NonF2fDoctorListBanner = styled.div`
  padding: 16px;
  align-items: center;
  align-self: stretch;
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  flex: 1 0 0;
  border-radius: 8px;
  background: ${({ theme }) => theme.color.gray40};
`;

const NonF2fDoctorListBannerContent = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 12px;
`;

const NonF2fDcotorListBannerTextArea = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;

const NonF2fDoctorListBannerTitle = styled.div`
  ${({ theme }) => `
      ${theme.typography.UI14M};
      color: ${theme.color.gray800};
    `}
`;
const NonF2fDoctorListBannerDescription = styled.div`
  ${({ theme }) => `
      ${theme.typography.UI12R};
      color: ${theme.color.gray500}
    `}
`;

const NeighborhoodSection = styled.section`
  ${({ theme }) => css`
    padding-bottom: 40px;
    border-bottom: 1px solid ${theme.color.gray50};
  `}
`;

const NeighborhoodTopWrapper = styled.div`
  padding: 32px 24px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const NeighborhoodTitle = styled.h2`
  ${({ theme }) => css`
    ${theme.typography.UI22SB}
    color: ${theme.color.gray900};
    white-space: pre-line;
  `}
`;

const NeighborhoodDescription = styled(HeaderDescription)``;

const MagazineSection = styled(NeighborhoodSection)``;

const SearchButton = styled.button.attrs({ type: 'button' })`
  ${({ theme }) => css`
    ${theme.typography.UI16SB}
    color: ${theme.color.gray600};
    background-color: ${theme.color.white};
    border: 1px solid ${theme.color.gray100};
  `}
  border-radius: 8px;
  cursor: pointer;
  padding: 0 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 48px;
`;

const PrimarySearchButton = styled(SearchButton)`
  ${({ theme }) => css`
    color: ${theme.color.primary700};
    background-color: ${theme.color.primary100};
    border: 1px solid ${theme.color.primary100};
  `}
`;

const ButtonWrapper = styled.div`
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DoctorButtonWrapper = styled(ButtonWrapper)`
  margin-top: 20px;
`;

const EmptyImage = styled.img`
  object-fit: cover;
`;

const EmptyButton = styled(SearchButton)`
  ${({ theme }) => css`
    color: ${theme.color.white};
    background-color: ${theme.color.primary600};
    border: 1px solid ${theme.color.primary600};
    width: 240px;
  `}
`;
